import React, { createContext, useContext, useEffect, useCallback } from 'react'
import { getConfig } from '@utils/config'

interface AnalyticsContextType {
  trackEvent: (eventName: string, parameters?: Record<string, any>) => void
  isInitialized: boolean
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined)

declare global {
  interface Window {
    dataLayer: any[]
    gtag: (...args: any[]) => void
  }
}

interface AnalyticsProviderProps {
  children: React.ReactNode
}

export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  const [isInitialized, setIsInitialized] = React.useState(false)

  useEffect(() => {
    const config = getConfig()
    const measurementId = config?.FIREBASE_CONFIG?.measurementId

    if (measurementId) {
      // Initialize dataLayer and gtag function
      window.dataLayer = window.dataLayer || []
      function gtag(...args: any[]) {
        window.dataLayer.push(args)
      }
      window.gtag = gtag

      // Dynamically load gtag script
      const script = document.createElement('script')
      script.async = true
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
      document.head.appendChild(script)

      // Configure gtag after script loads
      script.onload = function() {
        gtag('js', new Date())
        gtag('config', measurementId, {
          debug_mode: config?.DEBUG || false,
          send_page_view: true
        })

        setIsInitialized(true)

        // Only log in development environment
        if (config?.DEBUG) {
          console.log(`Google Analytics initialized for ${config?.ENVIRONMENT || 'unknown'} environment with ID: ${measurementId}`)
        }
      }

      return () => {
        // Cleanup script if component unmounts
        if (document.head.contains(script)) {
          document.head.removeChild(script)
        }
      }
    } else {
      console.warn('Google Analytics measurement ID not found in configuration')
    }
  }, [])

  const trackEvent = useCallback((eventName: string, parameters?: Record<string, any>) => {
    if (window.gtag && isInitialized) {
      window.gtag('event', eventName, parameters)
    } else if (!isInitialized) {
      // Queue the event for when analytics is initialized
      setTimeout(() => trackEvent(eventName, parameters), 100)
    }
  }, [isInitialized])

  return (
    <AnalyticsContext.Provider value={{ trackEvent, isInitialized }}>
      {children}
    </AnalyticsContext.Provider>
  )
}

export function useAnalytics() {
  const context = useContext(AnalyticsContext)
  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider')
  }
  return context
}
